import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, ScrollText } from "lucide-react";
import { listarAuditoria } from "../services/auditoria";

const ENTIDADES = ["", "projeto", "tarefa", "contrato", "parcela", "cliente", "usuario", "comentario"];

const ACAO_LABELS: Record<string, string> = {
  criar: "Criou",
  atualizar: "Atualizou",
  deletar: "Removeu",
  desativar: "Desativou",
  mover_status: "Mudou status",
  pagar: "Registrou pagamento",
  aprovar: "Aprovou",
  comentar: "Comentou em",
};

const ENT_BADGE: Record<string, string> = {
  projeto: "bg-blue-100 text-blue-700",
  tarefa: "bg-amber-100 text-amber-700",
  contrato: "bg-emerald-100 text-emerald-700",
  parcela: "bg-emerald-100 text-emerald-700",
  cliente: "bg-purple-100 text-purple-700",
  usuario: "bg-slate-100 text-slate-700",
  comentario: "bg-blue-100 text-blue-700",
};

export function Auditoria() {
  const [entidade, setEntidade] = useState("");
  const [q, setQ] = useState("");

  const { data: itens = [], isLoading } = useQuery({
    queryKey: ["auditoria", entidade, q],
    queryFn: () => listarAuditoria({ entidade: entidade || undefined, q: q || undefined, limit: 200 }),
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <ScrollText size={20} className="text-blue-600" /> Auditoria
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Trilha de ações do sistema (últimos {itens.length} eventos).</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, autor ou conteúdo…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500"
          />
        </div>
        <select value={entidade} onChange={(e) => setEntidade(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500">
          {ENTIDADES.map((e) => <option key={e} value={e}>{e ? e : "Todas as entidades"}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {isLoading && <p className="p-6 text-sm text-slate-400 text-center">Carregando…</p>}
        {!isLoading && itens.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">Nenhum evento encontrado.</p>}
        <ul className="divide-y divide-slate-100">
          {itens.map((i) => (
            <li key={i.id} className="px-4 py-3 hover:bg-slate-50">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${ENT_BADGE[i.entidade] || "bg-slate-100 text-slate-600"}`}>
                  {i.entidade}
                </span>
                <span className="text-sm">
                  <strong className="text-slate-800">{i.autorNome || "Sistema"}</strong>{" "}
                  <span className="text-slate-600">{ACAO_LABELS[i.acao] || i.acao}</span>{" "}
                  {i.entidadeLabel && <strong className="text-slate-700">"{i.entidadeLabel}"</strong>}
                </span>
                <span className="ml-auto text-[11px] text-slate-400">
                  {new Date(i.criadoEm).toLocaleString("pt-BR")}
                </span>
              </div>
              {/* Movimentação de card: mostra "De X → Para Y" de forma amigável,
                  em vez do JSON cru (é o caso mais consultado — "quem moveu"). */}
              {i.acao === "mover_status" && i.detalhes && (i.detalhes as any).paraLabel ? (
                <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{(i.detalhes as any).deLabel || "—"}</span>
                  <span className="text-slate-400">→</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">{(i.detalhes as any).paraLabel}</span>
                </div>
              ) : i.detalhes ? (
                <pre className="text-[11px] text-slate-500 mt-1 bg-slate-50 rounded p-2 overflow-x-auto">{JSON.stringify(i.detalhes, null, 2)}</pre>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
